import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { BaasicInput, Loader, BaasicDropdown, BaasicButton, BaasicTable } from 'core/components';
import { fulfillmentOrderStatus } from 'common/constants';
import { defaultTemplate } from 'core/hoc';
import { DeletePalletDropdownAction } from 'core/components/select';
import Barcode from 'react-barcode';
import _ from 'lodash';

const LabelingModeTemplate = ({
	t,
	labelingModeViewStore,
	labelingModeViewStore: {
		setInputs,
		onUpcScan,
		isUpcScanned,
		scanUpcMessage,
		steps,
		steps: { labelingStep },
		loading,
		activeStep,
		useRegularPalletizingMode,
	},
}) => {
	if (loading) return <Loader />;
	return (
		<div className="ReactModal__form">
			<div className="ReactModal__header">
				<h3 className="spc--bottom--sml">{t('FULFILLMENTORDER.LABELINGMODE.TITLE')}</h3>
			</div>
			<div className="ReactModal__body">
				{!useRegularPalletizingMode && <CombinedLabelingAndPalletizing viewStore={labelingModeViewStore} t={t} />}
				{(activeStep.number === 1 || activeStep.number === 2 || !isUpcScanned) && useRegularPalletizingMode && (
					<Fragment>
						<div className="col col-sml-5 col-med-5">
							<BaasicInput
								autoFocus={true}
								field={labelingStep.form.$('upc')}
								reference={setInputs}
								onKeyUp={onUpcScan}
								disabled={!useRegularPalletizingMode && palletDropdownStore.value == null}
							/>
						</div>
						{scanUpcMessage && scanUpcMessage.length != 0 && (
							<p className={'type--color--warning'}>{t(scanUpcMessage)}</p>
						)}
					</Fragment>
				)}
				{isUpcScanned && (
					<Fragment>
						{/* STEPS HEADER */}
						<Header {...{ steps, t, labelingModeViewStore }} />
						{/* STEPS */}
						{renderSteps(labelingModeViewStore, t)}
						{/* FOOTER */}
						<Footer labelingModeViewStore={labelingModeViewStore} t={t} />
					</Fragment>
				)}
			</div>
		</div>
	);
};

const renderSteps = (labelingModeViewStore, t) => {
	switch (labelingModeViewStore.activeStep.number) {
		case 1:
			return <SelectingFoStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		case 2:
			return <LabelingStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		case 3:
			return <DimensionsStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		case 4:
			return <FinalStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		default:
			return null;
	}
};

const CombinedLabelingAndPalletizing = observer(({ viewStore, t }) => {
	const {
		palletDropdownStore,
		togglePallet,
		steps: { labelingStep },
		setInputs,
		onUpcScan,
		scanUpcMessage,
		data,
		tableData,
		onPrintLabel,
		isUpcScanned,
		createPalletMode,
		palletDeleteConfirmModal,
		displayDeleteAction,
	} = viewStore;
	return (
		<Fragment>
			{createPalletMode && <PalletCreate viewStore={viewStore} t={t} />}
			{!isUpcScanned && !createPalletMode && (
				<Fragment>
					<div className="row">
						<div className="col col-sml-6 col-med-6">
							<div className="inputgroup spc--bottom--sml">
								<div className="inputgroup--main">
									<BaasicDropdown
										autoFocus
										dropdownStore={palletDropdownStore}
										optionOverride={{
											shouldDisplayCallback: value => displayDeleteAction(value),
											Component: DeletePalletDropdownAction,
											onClick: option =>
												palletDeleteConfirmModal.open(
													{
														message: ['LOOKUPDELETECONFIRM.PALLET', { name: option.label }],
													},
													option
												),
										}}
										label="FULFILLMENTORDER.PALLETIZINGMODE.PALLET"
									/>
								</div>
								<div className="inputgroup--aside align--v--bottom">
									<BaasicButton
										className="btn btn--med btn--primary spc--left--sml"
										onClick={() => togglePallet()}
										type="button"
										label="FULFILLMENTORDER.PALLETIZINGMODE.NEWPALLET"
									/>
								</div>
							</div>
						</div>
						<div className="col col-sml-6 col-med-6">
							<BaasicInput
								autoFocus={true}
								field={labelingStep.form.$('upc')}
								reference={setInputs}
								onKeyUp={onUpcScan}
								disabled={palletDropdownStore.value == null}
							/>
						</div>
					</div>
					{scanUpcMessage && scanUpcMessage.length != 0 && (
						<p className={'type--color--warning'}>{t(scanUpcMessage)}</p>
					)}
					{data.length > 0 && !isUpcScanned && (
						<div className="row">
							<div className="col col-sml-12 col-med-12 separator--primary spc--bottom--med">
								<BaasicTable
									{...{
										tableData,
										viewStore,
										actions: viewStore,
									}}
									data={data.slice()}
								/>
							</div>
						</div>
					)}
					<div className="row">
						<div className="col col-sml-4 col-med-4">
							<BaasicButton
								className="btn btn--med btn--primary spc--bottom--sml"
								type="button"
								label="PRINTPALLETLABEL"
								onClick={() => onPrintLabel(palletDropdownStore.value.extra.label)}
								disabled={palletDropdownStore.value == null || palletDropdownStore.value.extra.label === ''}
								icon="fa fa-print spc--right--tny"
							/>
						</div>
					</div>
				</Fragment>
			)}
		</Fragment>
	);
});

const PalletCreate = observer(({ viewStore, t }) => {
	const {
		palletCreateForm,
		barcode,
		setRef,
		carrierTypeServiceTypeDropdownStore,
		createPalletNotification,
		togglePallet,
	} = viewStore;
	return (
		<Fragment>
			<form className="ReactModal__form">
				<div className="ReactModal__body">
					<div className="spc--bottom--sml type--center">
						<Barcode format={'CODE39'} value={barcode} ref={ref => setRef(ref)} renderer={'img'} />
					</div>
					<div className="spc--bottom--sml">
						<BaasicInput field={palletCreateForm.$('name')} />
					</div>
					{createPalletNotification && createPalletNotification.includes('CONFLICT') && (
						<p className="type--color--warning spc--bottom--sml">{t(createPalletNotification)}</p>
					)}
					<BaasicDropdown
						dropdownStore={carrierTypeServiceTypeDropdownStore}
						className="spc--bottom--sml flex--grow--1"
					/>
				</div>
				{palletCreateForm.error != null && palletCreateForm.error}
				<div className="ReactModal__footer">
					<BaasicButton
						className="btn btn--med btn--primary spc--bottom--sml spc--right--sml"
						type="submit"
						onClick={palletCreateForm.onSubmit}
						label="SAVE"
					/>
					<BaasicButton
						className="btn btn--med btn--ghost spc--bottom--sml"
						type="button"
						onClick={togglePallet}
						label="BACK"
					/>
				</div>
			</form>
		</Fragment>
	);
});

const SelectingFoStep = ({
	labelingModeViewStore: {
		fulfillmentOrders,
		selectFulfillmentOrder,
		packagedFulfillmentOrders,
		product,
		handleShowAll,
	},
	t,
}) => {
	return (
		<Fragment>
			<div className="card card--shadow card--med spc--bottom--sml--alt">
				<div className="labeling__product">
					<div className="flex--grow--1">
						<div className="spc--bottom--sml--alt">
							<label className="type--xsml type--grey-5 type--uppercase">
								{t('FULFILLMENTORDER.LABELINGMODE.SELECTINGFOSTEP.PRODUCTSKU')}
							</label>
							<p>{product.mainSku}</p>
						</div>
						<div>
							<label className="type--xsml type--grey-5 type--uppercase">
								{t('FULFILLMENTORDER.LABELINGMODE.SELECTINGFOSTEP.PRODUCTNAME')}
							</label>
							<p>{product.name}</p>
						</div>
					</div>
					<div className="labeling__product__preview">
						<img src="https://milliardbedding.com/wp-content/uploads/2021/04/51104088640_40f294f443_o-scaled-700x700.jpg"></img>
					</div>
				</div>
			</div>
			<label className="labeling__subtitle spc--bottom--sml">
				{t('FULFILLMENTORDER.LABELINGMODE.ASSOCIATEDORDERS')}
			</label>
			{_.map(fulfillmentOrders, fulfillmentOrder => {
				return (
					<div key={fulfillmentOrder.fulfillmentOrderId} className="labeling__list">
						<TableHeader fulfillmentOrder={fulfillmentOrder} selectFulfillmentOrder={selectFulfillmentOrder} t={t} />
						<div className="labeling__list__body">
							<div className={`labeling__list__table__wrapper ` + (fulfillmentOrder.showAll ? '' : 'not-expanded')}>
								<table className="labeling__list__table">
									<thead>
										<tr>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.SKU')}:</th>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.QUANTITY')}:</th>
										</tr>
									</thead>
									<tbody>
										{_.map(fulfillmentOrder.fulfillmentOrderItems, item => {
											return (
												<tr key={item.id}>
													<td>{item.sku}</td>
													<td>{item.quantity}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div
								className={
									fulfillmentOrder.fulfillmentOrderItems.length <= 3 || fulfillmentOrder.showAll
										? 'display--none'
										: `type--center spc--top--sml`
								}
							>
								<button className="btn btn--sml btn--ghost" onClick={() => handleShowAll(fulfillmentOrder.id)}>
									{t('FULFILLMENTORDER.LABELINGMODE.SHOWALL')}
									<i className="icon icon--sml icon--arrow-down spc--left--tny"></i>
								</button>
							</div>
						</div>
					</div>
				);
			})}
			{/* LAST PACKAGED FULFILLMENT ORDERS */}
			{packagedFulfillmentOrders.length > 0 && (
				<Fragment>
					<label className="labeling__subtitle spc--bottom--sml">
						{t('FULFILLMENTORDER.LABELINGMODE.LASTPACKAGED')}
					</label>
					<ProcessedFulfillmentOrders fulfillmentOrders={packagedFulfillmentOrders} t={t} />
				</Fragment>
			)}
		</Fragment>
	);
};

const LabelingStep = observer(
	({
		labelingModeViewStore: {
			selectedFulfillmentOrder,
			openConfirmationModal,
			getRemoveItemConfirmModalProps,
			closeBoxMessage,
			boxedFulfillmentOrders,
			newFulfillmentOrderCreated,
		},
		t,
	}) => {
		return (
			<Fragment>
				{/* BOXED FULFILLMENT ORDERS - VISIBLE ONLY IF THE FULFILLMENT ORDER IS SPLITTED */}
				<div className="labeling__list">
					{boxedFulfillmentOrders.length > 0 && (
						<Fragment>
							<label className="labeling__subtitle spc--bottom--sml">
								{t('FULFILLMENTORDER.LABELINGMODE.PROCESSEDFOS')}
							</label>
							<ProcessedFulfillmentOrders fulfillmentOrders={boxedFulfillmentOrders} t={t} />
						</Fragment>
					)}
				</div>
				{/* CURRENTLY PROCESSING FULFILLMENT ORDER */}
				<div className="labeling__list">
					{newFulfillmentOrderCreated && (
						<label className="labeling__subtitle spc--bottom--sml">
							{t('FULFILLMENTORDER.LABELINGMODE.REMAININGSKUS')}
						</label>
					)}
					<TableHeader fulfillmentOrder={selectedFulfillmentOrder} t={t} />
					<div className="labeling__list__body">
						<table className="labeling__list__table">
							<thead>
								<tr>
									<th>{t('FULFILLMENTORDER.LABELINGMODE.SCANNED')}</th>
									<th>{t('FULFILLMENTORDER.LABELINGMODE.SKU')}</th>
									<th>{t('FULFILLMENTORDER.LABELINGMODE.QUANTITY')}</th>
									<th className="type--right">{t('FULFILLMENTORDER.LABELINGMODE.ACTIONS')}</th>
								</tr>
							</thead>
							<tbody>
								{_.map(selectedFulfillmentOrder.fulfillmentOrderItems, item => {
									const quantityLeft = item.scannedQuantity === 0 ? null : `${item.scannedQuantity}/${item.quantity}`;
									return (
										<tr key={item.id}>
											<td>
												<div className={'scan ' + (item.isScanned ? 'is-scanned' : '')}>
													<div className={' ' + (item.isScanned ? 'is-scanned' : '')} value={item} key={item.id}>
														{quantityLeft}
													</div>
												</div>
											</td>
											<td>{item.sku}</td>
											<td>{item.quantity}</td>
											{item.scannedQuantity > 0 ? (
												<td className="type--right" title={t('FULFILLMENTORDER.BULKPRINT.REMOVE')}>
													<i
														className="icon icon--med icon--unscan cursor--pointer"
														onClick={() => openConfirmationModal(getRemoveItemConfirmModalProps(item.product.id))}
													></i>
												</td>
											) : null}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					{closeBoxMessage && (
						<div className="col col-sml-12 spc--bottom--sml">
							<p className={'type--color--warning'}>{t(closeBoxMessage)}</p>
						</div>
					)}
				</div>
			</Fragment>
		);
	}
);

const DimensionsStep = observer(
	({
		labelingModeViewStore: {
			selectedFulfillmentOrder,
			createLabelMessage,
			steps: { dimensionsStep },
			showCreateTemplate,
			onCreateTemplateCancel,
			newFulfillmentOrderCreated,
			dimensionsStepErrorMessage,
			boxDropdownStore,
		},
		t,
	}) => {
		return (
			<Fragment>
				{newFulfillmentOrderCreated && (
					<div className="message message--tny message--primary message--primary--info spc--bottom--sml--alt">
						<p>{t('FULFILLMENTORDER.LABELINGMODE.NEWFULFILLMENTORDERCREATED')}</p>
					</div>
				)}
				<div className="labeling__list">
					<TableHeader fulfillmentOrder={selectedFulfillmentOrder} t={t} />
				</div>
				<div className="row">
					<div className="col col-sml-12 col-med-4 spc--bottom--sml">
						<BaasicDropdown
							dropdownStore={boxDropdownStore}
							isClearable={true}
							className="flex--grow--1"
							disabled={dimensionsStepErrorMessage}
						/>
					</div>
				</div>
				{dimensionsStepErrorMessage && (
					<p className={'type--color--warning spc--bottom--sml'}>{t(dimensionsStepErrorMessage)}</p>
				)}
				{showCreateTemplate && <BoxCreate {...{ dimensionsStep, onCreateTemplateCancel, t }} />}
				{createLabelMessage && createLabelMessage.length != 0 && (
					<p className={'type--color--warning spc--bottom--sml'}>{t(createLabelMessage)}</p>
				)}
			</Fragment>
		);
	}
);

const BoxCreate = ({ dimensionsStep, onCreateTemplateCancel, t }) => {
	return (
		<form className="box--primary box--sml spc--bottom--sml" onSubmit={dimensionsStep.form.onSubmit}>
			<div className="box__header">
				<h4 className="type--base type--color--grey-5">
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CREATEDIMENSIONSTEMPLATE')}
				</h4>
			</div>
			<div className="row row--form">
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('name')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.NAME')}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('height')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.HEIGHT')}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('width')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.WIDTH')}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('length')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.LENGTH')}
					/>
				</div>
			</div>

			<div>
				<button className="btn btn--sml btn--primary spc--right--sml" type="submit">
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CREATE')}
				</button>
				<button className="btn btn--sml btn--outline" type="button" onClick={() => onCreateTemplateCancel()}>
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CANCEL')}
				</button>
			</div>
		</form>
	);
};

const FinalStep = observer(
	({
		labelingModeViewStore: {
			setInputs,
			selectedFulfillmentOrder,
			onTrackingNumberScan,
			scanLabelMessage,
			steps: { finalStep },
			createLabelMessage,
		},
		t,
	}) => {
		return (
			<Fragment>
				<div className="labeling__list">
					<TableHeader fulfillmentOrder={selectedFulfillmentOrder} t={t} />
				</div>
				<div className="row">
					<div className="col col-sml-12 col-med-6">
						<BaasicInput
							autoFocus
							field={finalStep.form.$('trackingNumber')}
							reference={setInputs}
							onKeyUp={onTrackingNumberScan}
							disabled={selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan}
						/>
						{scanLabelMessage != null && (
							<p
								className={
									scanLabelMessage.includes('NOTFOUND') ||
									scanLabelMessage.includes('STATUSNOTLABELCREATED') ||
									scanLabelMessage.includes('CARRIERMISMATCH')
										? 'type--color--warning spc--bottom--sml'
										: 'type--color--success spc--bottom--sml'
								}
							>
								{t(scanLabelMessage)}
							</p>
						)}
					</div>
				</div>
				{createLabelMessage && createLabelMessage.length != 0 && (
					<p className={'type--color--warning spc--bottom--sml'}>{t(createLabelMessage)}</p>
				)}
			</Fragment>
		);
	}
);

const TableHeader = observer(({ fulfillmentOrder, selectFulfillmentOrder, t }) => {
	const status =
		fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.labelCreated
			? 'created'
			: fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.picking
			? 'picking'
			: fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.palletized ||
			  fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.packaged ||
			  fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.exitScan
			? 'final'
			: '';
	return (
		<div
			className={`labeling__list__header ` + status + (selectFulfillmentOrder ? ' is-clickable' : '')}
			key={fulfillmentOrder.id}
			onClick={selectFulfillmentOrder ? () => selectFulfillmentOrder(fulfillmentOrder) : null}
		>
			<div>
				<label>{t('FULFILLMENTORDER.LABELINGMODE.ID')}</label>
				<p>{fulfillmentOrder.fulfillmentOrderId}</p>
			</div>
			<div>
				<label>{t('FULFILLMENTORDER.LABELINGMODE.STATUS')}</label>
				<p className={`labeling__status ` + status}>{fulfillmentOrder.fulfillmentOrderStatus.name}</p>
			</div>
			<div>
				<label>{t('FULFILLMENTORDER.LABELINGMODE.MPO')}</label>
				<p>{fulfillmentOrder.order.orderNumber}</p>
			</div>
		</div>
	);
});

const ProcessedFulfillmentOrders = ({ fulfillmentOrders, t }) => {
	return (
		<Fragment>
			{_.map(fulfillmentOrders, fulfillmentOrder => {
				return (
					<div className="labeling__list">
						<TableHeader fulfillmentOrder={fulfillmentOrder} t={t} />
						<div className="labeling__list__body">
							<div className={`labeling__list__table__wrapper ` + (fulfillmentOrder.showAll ? '' : 'not-expanded')}>
								<table className="labeling__list__table">
									<thead>
										<tr>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.SKU')}:</th>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.QUANTITY')}:</th>
										</tr>
									</thead>
									<tbody>
										{_.map(fulfillmentOrder.fulfillmentOrderItems, item => {
											return (
												<tr key={item.id}>
													<td>{item.sku}</td>
													<td>{item.quantity}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div
								className={
									fulfillmentOrder.fulfillmentOrderItems.length <= 3 || fulfillmentOrder.showAll
										? 'display--none'
										: `type--center spc--top--sml`
								}
							>
								<button className="btn btn--sml btn--ghost" onClick={() => handleShowAll(fulfillmentOrder.id, true)}>
									{t('FULFILLMENTORDER.LABELINGMODE.SHOWALL')}
									<i className="icon icon--sml icon--arrow-down spc--left--tny"></i>
								</button>
							</div>
						</div>
					</div>
				);
			})}
		</Fragment>
	);
};

const Footer = observer(
	({
		labelingModeViewStore: {
			activeStep,
			returnToPreviousStep,
			closeBox,
			printLabel,
			printPackingSlip,
			steps: { dimensionsStep },
			loading,
			showCreateTemplate,
			dimensionsStepErrorMessage,
			closeBoxDisabled,
			onUndo,
			openAbortConfirmModal,
			boxedFulfillmentOrders,
			selectedFulfillmentOrder,
		},
		t,
	}) => {
		return (
			<div className="labeling__footer">
				{/* UNDO BUTTON */}
				{/* Visible only step 4 (Palletizing step) */}
				{activeStep.number === 4 && (
					<button
						className="btn btn--med btn--outline"
						type="button"
						onClick={onUndo}
						disabled={selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan}
					>
						{t('FULFILLMENTORDER.LABELINGMODE.UNDO')}
					</button>
				)}
				{/* BACK BUTTON AND CLOSE BOX BUTTON */}
				{/* Visible only on step 2 (Labeling step) */}
				{activeStep.number === 2 && (
					<Fragment>
						<button className="btn btn--med btn--outline" type="button" onClick={() => returnToPreviousStep()}>
							{t('BACK')}
						</button>
						<button
							className="btn btn--med btn--primary"
							type="button"
							disabled={closeBoxDisabled}
							onClick={() => closeBox()}
						>
							{t('FULFILLMENTORDER.LABELINGMODE.LABELINGSTEP.CLOSEBOX')}
						</button>
					</Fragment>
				)}
				{/* PRINT PACKING SLIP BUTTON */}
				{/* Visible only on step 3 and 4 (Dimenisons and Palletizing step) */}
				{(activeStep.number === 3 || activeStep.number === 4) &&
					selectedFulfillmentOrder.seller.sellerOrganizationInfo.printPackingSlip && (
						<button
							className="btn btn--med btn--outline"
							type="button"
							onClick={() => {
								printPackingSlip();
							}}
							disabled={dimensionsStepErrorMessage}
						>
							<i className="fa fa-print spc--right--tny"></i>
							{t('FULFILLMENTORDER.LABELINGMODE.PACKINGSLIP.PRINT')}
						</button>
					)}
				{/* PRINT LABEL BUTTON */}
				{/* Visible only on step 3 (Dimensions step) */}
				{activeStep.number === 3 && (
					<button
						className="btn btn--med btn--primary"
						type="button"
						disabled={
							!dimensionsStep.form.$('boxId').isValid || loading || showCreateTemplate || dimensionsStepErrorMessage
						}
						onClick={() => printLabel()}
					>
						{loading && (
							<div className="spinnercont spc--right--sml">
								<div className="spinner"></div>
							</div>
						)}
						<i className="fa fa-print spc--right--tny"></i>
						{t('FULFILLMENTORDER.LABELINGMODE.PRINTLABEL')}
					</button>
				)}
				{/* ABORT BUTTON */}
				{/* Visible on steps 3, 4 and 2 if order was split */}
				{(activeStep.number >= 3 || (activeStep.number === 2 && boxedFulfillmentOrders.length > 0)) && (
					<div className="flex--grow--1 type--right">
						<button
							className="btn btn--med btn--warning"
							type="button"
							onClick={() => openAbortConfirmModal()}
							disabled={selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan}
						>
							{t('FULFILLMENTORDER.LABELINGMODE.ABORT')}
						</button>
					</div>
				)}
			</div>
		);
	}
);

const Header = observer(({ steps, t, labelingModeViewStore }) => {
	const {
		palletDropdownStore,
		useRegularPalletizingMode,
		activeStep,
		steps: { labelingStep },
		setInputs,
		onUpcScan,
	} = labelingModeViewStore;
	return (
		<Fragment>
			<div className="row">
				{!useRegularPalletizingMode && (
					<Fragment>
						<div className="col col-sml-3 col-med-3">
							<p>{t('FULFILLMENTORDER.LABELINGMODE.PALLET')}</p>
							<p>{palletDropdownStore.value.label}</p>
						</div>
						{activeStep.number == 1 || activeStep.number == 2 ? (
							<div className="col col-sml-5 col-med-5">
								<BaasicInput
									field={labelingStep.form.$('upc')}
									reference={setInputs}
									onKeyUp={onUpcScan}
									disabled={!useRegularPalletizingMode && palletDropdownStore.value == null}
								/>
							</div>
						) : (
							<div className="col col-sml-5 col-med-5">
								<p>{t('FULFILLMENTORDER.LABELINGMODE.UPC')}</p>
								<p>{labelingStep.form.$('upc').value}</p>
							</div>
						)}
					</Fragment>
				)}
			</div>
			<div className="labeling__step">
				{_.map(steps, step => {
					return (
						<div className={'labeling__step__item'} key={`${step.order}-${step.title}`}>
							<div
								className={
									`labeling__step__number ` + (step.isCompleted ? 'is-completed' : step.isActive ? 'is-active' : '')
								}
							>
								{step.number}
							</div>
							<div className={'labeling__step__label'}>{t(step.title)}</div>
						</div>
					);
				})}
			</div>
		</Fragment>
	);
});

export default defaultTemplate(LabelingModeTemplate);



// WEBPACK FOOTER //
// ./src/themes/default/modules/order/pages/LabelingModeTemplate.jsx